import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { SECTION_LEVELS, type SectionLevelId } from '../models/layoutConfig.types'

import { useWorkspace } from '@/features/account'

// The group/level selector sources extracted from OrphexReportConfig.vue (v3 :289, :332-335,
// :509-520). Group names are workspace-authored `custom_grouping` attribute data (display-safe
// raw strings); the "Overall" entry and the level labels are component-side i18n.

interface CustomGroupingEntry {
  entity_type?: unknown
  name?: unknown
  /** The report column this group's values live in — the table's group-by field. */
  slot_name?: unknown
}

export interface UseSectionGroupsOptions {
  /** The active tab/section entity type; no entity type means no group select. */
  entityType: MaybeRefOrGetter<string | undefined>
  /** Shows the Ad/Creative level select (v3 `creativeLevel`, CreativeDashboard). */
  creativeLevel?: MaybeRefOrGetter<boolean | undefined>
}

export interface UseSectionGroups {
  /** Workspace custom-group names matching the entity type (v3 `getEntityGroupOptions`). */
  groupNames: ComputedRef<string[]>
  /** v3 showed the select only when it offered more than bare "Overall". */
  hasGroups: ComputedRef<boolean>
  levels: ComputedRef<readonly SectionLevelId[]>
  /** The report column backing a selected group name, or null when the name is unmatched
   *  (stale selection, entity type changed) — v3 `OrphexReportTable.changeGroupColumns`'s guard. */
  fieldForGroup: (name: string) => string | null
}

export function useSectionGroups(options: UseSectionGroupsOptions): UseSectionGroups {
  const workspace = useWorkspace()
  const entries = computed<CustomGroupingEntry[]>(() => {
    const entityType = toValue(options.entityType)
    if (!entityType) return []
    const raw = workspace.getAttribute('custom_grouping')?.value
    if (!Array.isArray(raw)) return []
    return (raw as CustomGroupingEntry[]).filter((entry) => entry?.entity_type === entityType)
  })

  const groupNames = computed<string[]>(() =>
    entries.value
      .map((entry) => entry.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0),
  )
  return {
    groupNames,
    hasGroups: computed(() => groupNames.value.length > 0),
    levels: computed(() => (toValue(options.creativeLevel) ? SECTION_LEVELS : [])),
    fieldForGroup: (name) => {
      const match = entries.value.find((entry) => entry.name === name)
      return typeof match?.slot_name === 'string' && match.slot_name.length > 0
        ? match.slot_name
        : null
    },
  }
}
